// @ts-nocheck
import TextField from "@store/MobiusOverrides/TextField/TextField";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { BillToModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import { styles } from "@insite/content-library/Widgets/ContactUs/AvailableTopics";
import Button, { ButtonProps } from "@insite/mobius/Button";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Search from "@insite/mobius/Icons/Search";
import Radio, { RadioProps } from "@insite/mobius/Radio";
import RadioGroup, { RadioGroupProps } from "@insite/mobius/RadioGroup";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useIsMobile from "@store/Framework/Common/Hooks/useIsMobile";
import { CustomerPoNumber } from "@store/Framework/Services/CustomerPoNumberService";
import SearchBox from "@store/Library/Components/Bunzl/Common/SearchBox";
import ManagePoCustomerDisplay from "@store/Library/Widgets/ManageDefaultPO/ManagePoCustomerDisplay";
import useExpandProps from "@toolkit/Framework/Common/Hooks/useExpandProps";
import { getMediaQuery } from "@toolkit/Framework/Common/Utilities/mediaQueries";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import React from "react";
import styled, { css } from "styled-components";

const desktopQuery = getMediaQuery({ option: "up", first: false, min: 701, max: 9999, last: true });
const mobileQuery = getMediaQuery({ option: "down", first: true, min: 0, max: 700, last: false });
interface ManageDefaultPoActionsStyles {
    subtitleGridItem?: GridItemProps;
    topSectionWrapper?: InjectableCss;
    selectorRadioGroup?: RadioGroupProps;
    radioWrapper?: InjectableCss;
    radio?: RadioProps;
    addressSectionWrapper?: InjectableCss;
    searchBoxGridItem?: GridItemProps;
    newPoOverrideButton?: ButtonProps;
    actionGridItem?: GridItemProps;
}

const manageDefaultPoActionsStyles: ManageDefaultPoActionsStyles = {
    radio: {
        css: css`
            ${mobileQuery} {
                width: 30%;
            }
        `,
    },
    newPoOverrideButton: {
        css: css`
            margin-top: 2px;
            display: flex;
            height: ${pxToRem(44)};
            align-items: center;
            :hover {
                background-color: ${getColor("primary")};
                border-color: ${getColor("primary")};
            }
            ${mobileQuery} {
                align-self: end;

                margin-top: 0;
            }
        `,
        variant: "secondary" as const,
    },

    topSectionWrapper: {
        css: css`
            width: 100%;
            display: flex;
            min-height: ${pxToRem(158)};
            justify-content: space-between;
            flex-wrap: wrap;
            row-gap: 1rem;
            @media (max-width: 767px) {
                row-gap: 0.25rem;
            }
        `,
    },

    searchBoxGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            padding-block: 0;
            ${mobileQuery} {
                padding: 0;
            }
        `,
        width: [8, 8, 11, 9, 9],
    },
    radioWrapper: {
        css: css`
            justify-content: space-between;
            display: flex;
            padding-top: 0.625rem;
            ${desktopQuery} {
                flex-direction: column;
                min-height: 5rem;
            }
            ${mobileQuery} {
                column-gap: 1rem;
                justify-content: space-evenly;
            }
        `,
    },
    selectorRadioGroup: {
        labelProps: {
            variant: "h5",
            css: css`
                ${mobileQuery} {
                    font-size: ${pxToRem(18)};
                }
            `,
        },
        css: css`
            ${desktopQuery} {
                display: flex;
                width: fit-content;
                flex-direction: column;
                gap: 1rem;
            }
        `,
    },
    subtitleGridItem: {
        width: [12, 12, 12, 7, 7],
    },
    actionGridItem: {
        width: 12,
        css: css`
            column-gap: 1rem;
            padding: 0;
            align-items: flex-end;

            ${mobileQuery} {
                padding-inline: 24px;
            }
        `,
    },
    addressSectionWrapper: {
        css: css`
            display: flex;
            min-width: 7rem;

            ${mobileQuery} {
                width: 100%;
                flex-direction: column;
                align-items: center;
                position: relative;
            }
        `,
    },
};
interface ManageDefaultPoActionsProps {
    selectedOverride: "billTo" | "shipTo" | undefined;
    setSelectedOverride: (override: "billTo" | "shipTo" | undefined) => void;
    setSearchText: (searchText: string) => void;
    extendedStyles?: ManageDefaultPoActionsStyles;
    overrideStyles?: ManageDefaultPoActionsStyles;
    billTo: BillToModel;
    shipTo: ShipToModel;
    handleNewPo: () => void;
    editingPo: CustomerPoNumber | undefined;
    newPo: CustomerPoNumber | undefined;
    searchText: string;
}

const Triangle = styled.div<{ left: number }>`
    position: relative;
    clip-path: polygon(50% 0%, 0% 100%, 100% 100%);
    background-color: ${getColor("primary")};
    width: 1.25rem;
    height: 1.25rem;
    transition: left 0.5s ease-in-out;
    left: ${({ left }) => left}%;
    ${desktopQuery} {
        display: none;
    }
`;
const ManageDefaultPoActions: React.FC<ManageDefaultPoActionsProps> = ({
    billTo,
    shipTo,
    searchText,
    setSelectedOverride,
    extendedStyles,
    overrideStyles,
    newPo,
    editingPo,
    handleNewPo,
    selectedOverride,
    setSearchText,
}) => {
    const isMobile = useIsMobile();
    const styles = useExpandProps(manageDefaultPoActionsStyles, extendedStyles, overrideStyles);
    const isBillTo = selectedOverride === "billTo";
    return (
        <>
            <GridItem {...styles.subtitleGridItem}>
                <StyledWrapper {...styles.topSectionWrapper}>
                    <RadioGroup
                        horizontal={isMobile || false}
                        value={selectedOverride}
                        label={translate("Choose Billing or Shipping Address")}
                        {...styles.selectorRadioGroup}
                    >
                        <StyledWrapper {...styles.radioWrapper}>
                            <Radio
                                {...styles.radio}
                                value="billTo"
                                data-test-selector="Bill_To_Radio"
                                onClick={() => setSelectedOverride("billTo")}
                            >
                                {translate("Billing Address")}
                            </Radio>
                            <Radio
                                {...styles.radio}
                                value="shipTo"
                                data-test-selector="Ship_To_Radio"
                                onClick={() => setSelectedOverride("shipTo")}
                            >
                                {translate("Shipping Address")}
                            </Radio>
                        </StyledWrapper>
                    </RadioGroup>
                    <StyledWrapper {...styles.addressSectionWrapper}>
                        <Triangle left={isBillTo ? -35 : 11.25} />
                        <ManagePoCustomerDisplay
                            linkLabel={translate("Change")}
                            key={isBillTo.toString()}
                            customer={isBillTo ? billTo : shipTo}
                            label={translate(`Current ${isBillTo ? "Billing" : "Shipping"} Address`)}
                        />
                    </StyledWrapper>
                </StyledWrapper>
            </GridItem>
            <GridItem {...styles.actionGridItem}>
                <SearchBox
                    extendedStyles={{
                        gridItem: styles.searchBoxGridItem,
                        textField: {
                            iconProps: {
                                src: Search,
                                css: css`
                                    // search icon on mobile is overlapping with the clear icon
                                    ${mobileQuery} {
                                        ${searchText && "display: none;"}
                                    }
                                `,
                            },
                        },
                    }}
                    onSearch={setSearchText}
                    debounce={0}
                    placeholder={translate("Search")}
                />
                <Button
                    disabled={!!editingPo || !!newPo || !selectedOverride}
                    data-test-selector="Add_New_PO_Button"
                    {...styles.newPoOverrideButton}
                    sizeVariant={"small"}
                    onClick={handleNewPo}
                    mergeCss
                >
                    {translate("Add New PO")}
                </Button>
            </GridItem>
        </>
    );
};

export default ManageDefaultPoActions;
